import { useMemo } from 'react';
import type { CatalogEntry } from '../../../api/admin';
import type { ConnectionRow, DeliveryRow, WorkflowEdge, WorkflowGraph, WorkflowNode } from '../../../api/forms';
import { evalWhen } from '../../../form-engine/rules';
import { branchExits, branchPortFor, replayPath, topoOrder } from './graph';
import { BranchExitsRow, CARD, edgePath, fanTurns, portX, widthOf } from './WorkflowCanvas';
import './workflow.css';

/**
 * The path one submission actually took.
 *
 * Nothing about which steps ran is stored: the playbook is firm that this
 * reads back from the delivery rows the pipeline already writes and adds no
 * new table. So the path is RECOMPUTED the way the engine computed it — walk
 * from the start, and at each branch ask its condition of the answers this
 * person actually gave — and every node is then matched against the deliveries
 * that landed.
 *
 * That has one honest limit, and this component says it out loud rather than
 * presenting a reconstruction as a recording: the drawing is the CURRENT one.
 * A workflow edited since a submission ran is replayed as it stands today.
 *
 * The Deliveries tab stays exactly as it was. A table is better than a diagram
 * for "what failed and what did it say"; this answers the other question —
 * which way did this one go, and why.
 */

const heightOf = (n: WorkflowNode) =>
  (n.kind === 'start' || n.kind === 'merge' ? 36 : n.kind === 'end' ? 46 : n.kind === 'branch' ? 88 : 60);

/** How a delivery reads on a card: the word, and the tone it carries. */
function outcomeOf(d: DeliveryRow | undefined): { label: string; tone: string } | null {
  if (!d) return null;
  if (d.status === 'success') return { label: 'sent', tone: 'ok' };
  if (d.status === 'failed') return { label: 'failed', tone: 'danger' };
  if (d.status === 'skipped') {
    const res = (d.response || {}) as Record<string, unknown>;
    if (res.dryRun) return { label: 'test mode', tone: 'caution' };
    return { label: 'skipped', tone: 'neutral' };
  }
  return { label: d.status, tone: 'neutral' };
}

export default function RunView({
  graph, data, deliveries, connections, catalog, onRetry,
}: {
  graph: WorkflowGraph;
  /** The answers this submission gave — what the branches were asked. */
  data: Record<string, unknown>;
  deliveries: DeliveryRow[];
  connections: ConnectionRow[];
  catalog: CatalogEntry[];
  /** Run one step again. Absent while a re-send is already in flight. */
  onRetry?: (deliveryId: number) => void;
}) {
  const connById = useMemo(() => new Map(connections.map((c) => [c.id, c])), [connections]);
  const specOf = (type?: string) => catalog.find((c) => c.type === type);
  const byId = useMemo(() => new Map(graph.nodes.map((n) => [n.id, n])), [graph]);

  const path = useMemo(
    () => replayPath(graph, data, (node) => branchPortFor(node, data, (group, values) => {
      const g = (group || {}) as { match?: string; when?: { field: string; op: string; value?: string }[] };
      const conds = g.when || [];
      if (!conds.length) return false;
      return g.match === 'any'
        ? conds.some((c) => evalWhen(c as never, values))
        : conds.every((c) => evalWhen(c as never, values));
    })),
    [graph, data]
  );

  /** The delivery for a node, newest last — a re-send adds a row, not a state. */
  const deliveryFor = (node: WorkflowNode) =>
    [...deliveries].reverse().find((d) => d.connection_id === node.connectionId);

  const numbers = useMemo(() => {
    const out: Record<string, number> = {};
    let n = 0;
    for (const node of topoOrder(graph)) {
      if (!path.nodes.has(node.id) || !node.connectionId) continue;
      out[node.id] = ++n;
    }
    return out;
  }, [graph, path]);

  /** A delivery whose connection no step points at any more. */
  const strays = deliveries.filter(
    (d) => d.connection_id && !graph.nodes.some((n) => n.connectionId === d.connection_id)
  );

  const extent = graph.nodes.reduce(
    (acc, n) => ({ w: Math.max(acc.w, n.x + widthOf(n) + 60), h: Math.max(acc.h, n.y + heightOf(n) + 80) }),
    { w: 600, h: 360 }
  );

  // A branch fanning out to three or more places turns each arrow at its own
  // height, exactly as the canvas draws it.
  const turns = fanTurns(graph, (e: WorkflowEdge) => {
    const from = byId.get(e.from);
    const to = byId.get(e.to);
    if (!from || !to) return { x1: 0, y1: 0, x2: 0, y2: 0 };
    return { x1: from.x + portX(from, e.port), y1: from.y + heightOf(from), x2: to.x + widthOf(to) / 2, y2: to.y };
  });

  return (
    <div className="wf-run">
      <div className="wf-run-note">
        The path this submission took, replayed from its answers against the workflow
        <b> as it is drawn now</b>. Steps it did not reach are dimmed.
      </div>
      <div className="wf-surface wf-run-surface" style={{ height: Math.min(extent.h, 520) }}>
        <div className="wf-scroll" style={{ width: extent.w, height: extent.h }}>
          <svg className="wf-edges" width={extent.w} height={extent.h}>
            {graph.edges.map((e) => {
              const from = byId.get(e.from);
              const to = byId.get(e.to);
              if (!from || !to) return null;
              const took = path.ports.has(`${e.from}\n${e.port}`);
              const x1 = from.x + portX(from, e.port);
              const y1 = from.y + heightOf(from);
              const x2 = to.x + widthOf(to) / 2;
              const stroke = took ? 'var(--accent)' : 'var(--edge)';
              return (
                <g key={`${e.from}-${e.port}-${e.to}`} opacity={took ? 1 : 0.45}>
                  <path d={edgePath(x1, y1, x2, to.y, turns.get(e))} stroke={stroke} strokeWidth={took ? 2.5 : 2} fill="none" />
                  <path d={`M${x2 - 4} ${to.y - 8} L${x2 + 4} ${to.y - 8} L${x2} ${to.y} Z`} fill={stroke} />
                </g>
              );
            })}
          </svg>

          {graph.nodes.map((node) => {
            const took = path.nodes.has(node.id);
            const conn = node.connectionId ? connById.get(node.connectionId) : undefined;
            const delivery = deliveryFor(node);
            const outcome = took ? outcomeOf(delivery) : null;
            const isEnd = node.kind === 'end';
            const reached = isEnd && path.endedAt === node.id;
            const width = widthOf(node);

            return (
              <div
                key={node.id}
                className={[
                  'wf-node',
                  node.kind === 'start' ? 'pill' : '',
                  isEnd ? 'ending' : '',
                  node.kind === 'merge' ? 'rejoin' : '',
                  took ? 'took' : 'untaken',
                  reached ? 'selected' : '',
                ].filter(Boolean).join(' ')}
                style={{ left: node.x, top: node.y, position: 'absolute', ...(width !== CARD ? { width } : {}) }}
              >
                <div className="card">
                  {node.kind === 'start' ? 'A submission arrives'
                    : node.kind === 'merge' ? <>↳ The paths rejoin</> : (
                      <>
                        <div className="wf-body">
                          <span className={`wf-tile${node.kind === 'branch' ? ' routing'
                            : node.kind === 'mint' || node.kind === 'chase' ? ' identity' : ''}`}
                          >
                            {node.kind === 'branch' ? '?' : node.kind === 'mint' ? '🔗'
                              : node.kind === 'chase' ? '⏰' : isEnd ? '⚑'
                                : specOf(conn?.type)?.icon || '🔌'}
                          </span>
                          <span className="wf-text">
                            <span className="wf-over">
                              {node.kind === 'branch' ? 'Branch on an answer'
                                : isEnd ? 'Ending'
                                  : specOf(conn?.type)?.title || conn?.type || node.kind}
                            </span>
                            <span className="wf-name">
                              {node.kind === 'branch' ? String(node.config?.name || 'Which way?')
                                : isEnd ? (node.config?.mode === 'continue' ? 'Into the follow-up form'
                                  : String(node.config?.title || 'Thank you'))
                                  : conn?.name || node.kind}
                            </span>
                            {/* A branch quotes the answer that decided it — the
                                one fact that makes the picture explain itself. */}
                            {node.kind === 'branch' && took && (
                              <span className="wf-fact">
                                {(() => {
                                  const field = (node.config?.when as { when?: { field: string }[] })?.when?.[0]?.field;
                                  const answer = field ? data[field] : undefined;
                                  const exit = branchExits(node).find((x) => path.ports.has(`${node.id}\n${x.port}`));
                                  const side = exit?.port === 'yes' ? 0 : 1;
                                  // The first and last paths keep the words they always had;
                                  // a further path is named by its label, or by its place.
                                  const extra = exit && exit.port !== 'yes' && exit.port !== 'no';
                                  const label = extra ? exit.label || exit.fallback : exit?.label;
                                  return field
                                    ? `${field} was “${String(answer ?? '')}” — took ${label || (side ? 'the other path' : 'the first path')}`
                                    : `took ${label || 'the other path'}`;
                                })()}
                              </span>
                            )}
                            {delivery?.last_error && took && (
                              <span className="wf-run-error">{delivery.last_error}</span>
                            )}
                          </span>
                          {outcome && <span className={`fs-pill ${outcome.tone}`}>{outcome.label}</span>}
                          {!outcome && took && numbers[node.id] === undefined && !isEnd
                            && node.kind !== 'branch' && (
                            <span className="fs-pill neutral">not run</span>
                          )}
                        </div>
                        {node.kind === 'branch' && (
                          <BranchExitsRow node={node} took={(port) => took && path.ports.has(`${node.id}\n${port}`)} />
                        )}
                      </>
                    )}
                  {!took && node.kind !== 'start' && <span className="wf-run-tag">not taken</span>}
                </div>
                {/* A failure does not stop its path — the drawing shows the
                    path carrying on past it, which is the whole point of
                    showing it here rather than only in the table. */}
                {took && delivery?.status === 'failed' && onRetry && (
                  <button className="fs-btn neutral sm wf-run-retry" onClick={() => onRetry(delivery.id)}>
                    Run this step again
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {strays.length > 0 && (
        <div className="wf-run-note stray">
          {strays.length} {strays.length === 1 ? 'delivery' : 'deliveries'} came from a step the workflow
          no longer has — {strays.map((d) => d.connection_type).join(', ')}. The Deliveries tab has them.
        </div>
      )}
    </div>
  );
}
